import React from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';

import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Autocomplete, Box, Drawer, MenuItem, Modal, Select, TextField, Typography } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';

import CustomButton from 'components/Button';
import { Header } from 'components/GlobalStyles/styles';
import LabelledDatePicker from 'components/LabelledDatePicker';
import { TextField as DisplayField } from 'components/LabelledTextField';
import LabelledTextField from 'components/LabelledTextField';
import { MessageType } from 'components/SnackBar';
import {
  useArchiveProjectMutation,
  useCreateProjectMutation,
  useCreateProjectScopeMutation,
  useUpdateProjectByIdMutation,
} from 'services/busmanApi';
import { ClientProjectResponse, ProjectRequest, ProjectResponse } from 'types/ProjectTypes';
import { JobManagerType } from 'types/WorkLogTypes';
import { PROJECT_SCOPE_STATUS, PROJECT_STATUS } from 'utils/constants';
import { ProjectModalSchema } from 'utils/schema';

import { ColContainer, FormSection, RowContainer } from '../styles';

const defaultProjectValues = {
  name: '',
  manager: [],
  project_id: '',
  description: '',
  client_contact: '',
  status: String(PROJECT_STATUS.pending.value),
  start_date: null,
  finish_date: null,
  estimated_cost: 0,
  percent_complete: 0,
  current_scope: null,
  estimated_hours: 0,
  current_estimated_cost: 0,
  current_estimated_hours: 0,
};

interface ProjectModalFormProps {
  open: boolean;
  isEditing?: boolean;
  onClose: () => void;
  initialValues: ProjectRequest | null;
  setSnackBarMessage: (message: MessageType) => void;
  formOptions: {
    managers: Array<JobManagerType>;
    clients: Array<ClientProjectResponse>;
  };
}

const ProjectModal: React.FC<ProjectModalFormProps> = ({
  initialValues,
  open,
  onClose,
  isEditing,
  formOptions,
  setSnackBarMessage,
}) => {
  const [deleteProject, setDeleteProject] = React.useState<boolean>(false);

  const [addNewProject] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectByIdMutation();
  const [archiveProject] = useArchiveProjectMutation();
  const [createScope] = useCreateProjectScopeMutation();

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    setValue,
  } = useForm({
    defaultValues: initialValues ?? defaultProjectValues,
    resolver: zodResolver(ProjectModalSchema),
  });

  const selectedClientId = useWatch({ control, name: 'client_contact' });
  const selectedClient = formOptions.clients.find((client) => client.id === selectedClientId);

  const onSubmit = async (data: ProjectRequest) => {
    const payload = {
      ...data,
      start_date: data.start_date ? dayjs(data.start_date).format('YYYY-MM-DD') : null,
      finish_date: data.finish_date ? dayjs(data.finish_date).format('YYYY-MM-DD') : null,
    };
    const showResult = (response: { error?: unknown }, verb: 'adding' | 'updating' | 'deleting', pastTense: string) => {
      setSnackBarMessage(
        response.error
          ? { type: 'error', msg: `Error ${verb} project` }
          : { type: 'success', msg: `Project ${pastTense} successfully.` },
      );
    };

    if (deleteProject && isEditing && initialValues) {
      showResult(await archiveProject(initialValues.project_id), 'deleting', 'deleted');
    } else if (isEditing && initialValues) {
      showResult(await updateProject(payload), 'updating', 'updated');
    } else {
      const response = (await addNewProject(payload)) as { data?: ProjectResponse; error?: unknown };
      showResult(response, 'adding', 'added');

      if (!response.error && response.data?.id) {
        await createScope({
          project: response.data.id,
          version: '1.0',
          status: PROJECT_SCOPE_STATUS.draft.value,
          change_summary: '',
          cost_delta: 0,
          hours_delta: 0,
          is_client_approved: false,
          parent_scope: null,
          effective_from: null,
          effective_till: null,
        });
      }
    }

    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          role="presentation"
          sx={{ borderRadius: 2, width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', p: { xs: 2, sm: 3 } }}>
          <Header>
            <Typography variant="h4">{isEditing ? 'Edit Project' : 'New Project'}</Typography>
            <Box onClick={onClose} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>
          <FormSection>
            <RowContainer>
              <LabelledTextField name="name" label="TITLE" control={control} />
            </RowContainer>
            <RowContainer>
              <Controller
                name="client_contact"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const clients = formOptions.clients || [];
                  return (
                    <ColContainer>
                      <Typography variant="body2">CLIENT CONTACT</Typography>
                      <Autocomplete
                        size="small"
                        options={clients}
                        value={clients.find((client) => client.id === field.value) || null}
                        getOptionLabel={(option) => option.full_name || ''}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={(event, value) => {
                          field.onChange(value?.id || '');
                          setValue('client_contact', value?.id || '');
                        }}
                        ListboxProps={{
                          style: {
                            fontSize: '12px',
                          },
                        }}
                        sx={{ width: '100%' }}
                        data-testid="client-select-option"
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        renderInput={(params) => <TextField {...params} required error={!!error} />}
                      />
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </ColContainer>
                  );
                }}
              />
            </RowContainer>
            <RowContainer>
              <Controller
                name="manager"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const managers = formOptions.managers || [];
                  return (
                    <ColContainer>
                      <Typography variant="body2">MANAGER</Typography>
                      <Autocomplete
                        multiple
                        size="small"
                        options={managers}
                        value={managers.filter((manager) => (field.value || []).includes(manager.user_id))}
                        getOptionLabel={(option) => option.full_name || ''}
                        isOptionEqualToValue={(option, value) => option.user_id === value?.user_id}
                        onChange={(event, value) => {
                          const ids = value.map((m) => m.user_id);
                          field.onChange(ids);
                          setValue('manager', ids);
                        }}
                        ListboxProps={{ style: { fontSize: '12px' } }}
                        sx={{ width: '100%' }}
                        data-testid="manager-select-option"
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        renderInput={(params) => <TextField {...params} required error={!!error} />}
                      />

                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </ColContainer>
                  );
                }}
              />
              <Controller
                name="status"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const status = PROJECT_STATUS;
                  return (
                    <ColContainer>
                      <Typography variant="body2">STATUS</Typography>
                      <Select
                        size="small"
                        onChange={(selectedItem) => {
                          field.onChange(selectedItem.target.value);
                          setValue('status', selectedItem.target.value);
                        }}
                        sx={{
                          width: '100%',
                        }}
                        label="status"
                        value={field.value}
                        error={!!error}
                        data-testid="status-select-option">
                        {Object.entries(status).map(([key, value]) => (
                          <MenuItem key={key} value={String(value.value)}>
                            {value.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </ColContainer>
                  );
                }}
              />
            </RowContainer>
            <LabelledTextField name="description" label="DESCRIPTION" control={control} multiline />

            {/* Primary Contact */}
            <Box sx={{ my: 2 }}>
              <Typography variant="h5">Primary Contact</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                <RowContainer>
                  <DisplayField label="POSITION" value={selectedClient?.position ?? ''} disabled />
                  <DisplayField label="FULL NAME" value={selectedClient?.full_name ?? ''} disabled />
                </RowContainer>
                <RowContainer>
                  <DisplayField label="EMAIL" value={selectedClient?.email ?? ''} disabled />
                  <DisplayField label="PHONE" value={selectedClient?.phone ?? ''} disabled />
                </RowContainer>
              </Box>
            </Box>

            {/* Time */}
            <Box sx={{ my: 2 }}>
              <Typography variant="h5">Time</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                <RowContainer>
                  <LabelledDatePicker name="start_date" label="START (PROJECTED)" control={control} />
                  <LabelledDatePicker name="finish_date" label="FINISH (PROJECTED)" control={control} />
                </RowContainer>
              </Box>
            </Box>

            {/* Cost */}
            <Box sx={{ my: 2 }}>
              <Typography variant="h5">Cost</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                <RowContainer>
                  <LabelledTextField name="estimated_cost" label="COST (PROJECTED)" control={control} />
                  <LabelledTextField name="current_estimated_cost" label="COST (LIVE)" control={control} />
                </RowContainer>
              </Box>
            </Box>

            <RowContainer sx={{ justifyContent: `${isEditing ? 'space-between' : 'flex-end'}`, mt: 2 }}>
              {isEditing && (
                <CustomButton
                  text={'Delete'}
                  variant="contained_red"
                  width="medium"
                  testid={'add-project-modal-btn'}
                  onClick={() => setDeleteProject(true)}
                />
              )}
              <CustomButton
                text={`${isEditing ? 'Update' : 'Add'}`}
                variant="contained"
                width="medium"
                testid={'add-project-modal-btn'}
                onClick={handleSubmit(onSubmit)}
                disabled={isEditing && !isDirty}
              />
            </RowContainer>
          </FormSection>
        </Box>
      </Drawer>
      {deleteProject && initialValues && (
        <Modal
          open={deleteProject}
          onClose={() => setDeleteProject(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box
            sx={{
              p: 2,
              top: '50%',
              left: '50%',
              width: 450,
              position: 'absolute',
              bgcolor: 'background.paper',
              transform: 'translate(-50%, -50%)',
              boxShadow:
                '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
            }}>
            <Header>
              <Typography variant="h4">Delete Project</Typography>
              <Box onClick={() => setDeleteProject(false)} sx={{ cursor: 'pointer' }}>
                <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
              </Box>
            </Header>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Are you sure you want to delete {initialValues.name}? All jobs and task will be removed from this project.
            </Typography>
            <RowContainer sx={{ justifyContent: 'flex-end', mt: 2 }}>
              <CustomButton
                text={'Delete'}
                variant="contained_red"
                width="medium"
                testid={'add-project-modal-btn'}
                onClick={handleSubmit(onSubmit)}
              />
            </RowContainer>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default ProjectModal;
